import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import { bucketService } from '../services/bucketService';
import './BucketVisualizer.css';

/* ============================================
   Types
   ============================================ */

interface BucketAllocation {
  bucket: 1 | 2 | 3 | 4;
  name: string;
  amount: number;
  percent: number;
  color: string;
  description: string;
  where: string;
  timeline: string;
  target?: string;
}

interface Projection {
  years: number;
  totalInvested: number;
  projectedValue: number;
  monthlyDividends: number;
}

interface AllocationResult {
  monthlyAmount: number;
  lifeStage: string;
  buckets: BucketAllocation[];
  insight: string;
  crashStrategy?: string;
  projection?: Projection;
}

type LifeStage = 'starting' | 'building' | 'approaching';
type InvestMode = 'monthly' | 'onetime';

interface Question {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

interface FlywheelRow {
  year: number;
  portfolio: number;
  annualDividends: number;
  monthlyDividends: number;
  dividendPercent: number;
}

/* ============================================
   Constants
   ============================================ */

const STAGE_INFO: Record<LifeStage, { emoji: string; title: string; age: string; netWorth: string; focus: string }> = {
  starting: {
    emoji: '\u{1F331}',
    title: 'STARTING OUT',
    age: 'Age: 18-35',
    netWorth: 'Net Worth: <$50K',
    focus: 'Focus: Build foundation',
  },
  building: {
    emoji: '\u{1F680}',
    title: 'BUILDING WEALTH',
    age: 'Age: 35-50',
    netWorth: 'Net Worth: $50K-$500K',
    focus: 'Focus: Grow',
  },
  approaching: {
    emoji: '\u{1F3AF}',
    title: 'APPROACHING FREEDOM',
    age: 'Age: 50+',
    netWorth: 'Net Worth: $500K+',
    focus: 'Focus: Protect + Transition',
  },
};

const STAGE_QUESTIONS: Record<LifeStage, Question[]> = {
  starting: [
    {
      key: 'emergencyFund',
      label: 'Emergency Fund Status:',
      options: [
        { value: 'not_started', label: "Haven't started yet" },
        { value: 'building', label: 'Building (have some saved)' },
        { value: 'full', label: 'Full (3-6 months expenses)' },
      ],
    },
    {
      key: 'shortTermGoals',
      label: 'Any big purchases in the next 1-5 years?',
      options: [
        { value: 'house', label: 'Yes \u2014 House down payment' },
        { value: 'car', label: 'Yes \u2014 Car' },
        { value: 'wedding', label: 'Yes \u2014 Wedding / Other major expense' },
        { value: 'none', label: 'No major purchases planned' },
      ],
    },
    {
      key: 'debt',
      label: 'Current debt situation:',
      options: [
        { value: 'high', label: 'High-interest debt (credit cards, >7% loans)' },
        { value: 'low', label: 'Only low-interest debt (mortgage, student loans <5%)' },
        { value: 'none', label: 'Debt-free' },
      ],
    },
  ],
  building: [
    {
      key: 'emergencyFund',
      label: 'Emergency Fund:',
      options: [
        { value: 'need_rebuild', label: 'Need to rebuild/increase' },
        { value: 'full', label: 'Full (3-6 months expenses)' },
      ],
    },
    {
      key: 'majorGoal',
      label: 'Major goals in next 5-10 years:',
      options: [
        { value: 'college', label: "Kids' college fund" },
        { value: 'property', label: 'Home upgrade / Second property' },
        { value: 'career', label: 'Career change flexibility fund' },
        { value: 'none', label: 'No specific goals \u2014 maximize growth' },
      ],
    },
    {
      key: 'riskTolerance',
      label: 'Risk tolerance:',
      options: [
        { value: 'conservative', label: 'Conservative \u2014 Prefer stability' },
        { value: 'moderate', label: 'Moderate \u2014 Balanced approach' },
        { value: 'aggressive', label: 'Aggressive \u2014 Maximize growth' },
      ],
    },
  ],
  approaching: [
    {
      key: 'yearsToFreedom',
      label: 'How close to your Freedom Number?',
      options: [
        { value: '10plus', label: '10+ years away' },
        { value: '5to10', label: '5-10 years away' },
        { value: 'less5', label: 'Less than 5 years' },
        { value: 'ready', label: 'Already there / Could retire now' },
      ],
    },
    {
      key: 'currentBucketStatus',
      label: 'Current bucket status:',
      options: [
        { value: 'growth', label: 'Mostly in growth (Bucket 4)' },
        { value: 'building_buffer', label: 'Starting to build buffer (Buckets 2-3)' },
        { value: 'setup', label: 'Already have withdrawal buckets set up' },
      ],
    },
    {
      key: 'incomeStability',
      label: 'Income stability:',
      options: [
        { value: 'stable', label: 'Stable career, steady income' },
        { value: 'variable', label: 'Variable income / Self-employed' },
        { value: 'reducing', label: 'Planning to reduce work soon' },
      ],
    },
  ],
};

/* ============================================
   Utility Functions
   ============================================ */

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

function calculateProjection(monthlyAmount: number, years: number): Projection {
  const rate = 0.08;
  const mr = rate / 12;
  const months = years * 12;
  const totalInvested = monthlyAmount * months;
  const projectedValue = monthlyAmount * ((Math.pow(1 + mr, months) - 1) / mr);
  const monthlyDividends = (projectedValue * 0.02) / 12;
  return { years, totalInvested: Math.round(totalInvested), projectedValue: Math.round(projectedValue), monthlyDividends: Math.round(monthlyDividends) };
}

const GOAL_LABELS: Record<string, string> = {
  house: 'House down payment',
  car: 'Car purchase fund',
  wedding: 'Wedding / Major expense fund',
};

function calculateAllocation(amount: number, stage: LifeStage, ans: Record<string, string>): AllocationResult {
  /* ---------- STARTING OUT ---------- */
  if (stage === 'starting') {
    const ef = ans.emergencyFund;
    const hasGoals = ans.shortTermGoals !== 'none';
    const goalDesc = GOAL_LABELS[ans.shortTermGoals] || '';

    if (ans.debt === 'high') {
      return {
        monthlyAmount: amount, lifeStage: stage,
        buckets: [
          { bucket: 1, name: 'SAFETY', amount: amount * 0.15, percent: 15, color: '#22c55e', description: 'Minimum emergency fund', where: 'HYSA (4-5% APY)', timeline: 'Until $2-3K saved' },
          { bucket: 2, name: 'DEBT PAYOFF', amount: amount * 0.50, percent: 50, color: '#ef4444', description: 'Attack high-interest debt', where: 'Extra payments on highest rate', timeline: 'Until <7% interest only' },
          { bucket: 4, name: 'LONG-TERM GROWTH', amount: amount * 0.35, percent: 35, color: '#8b5cf6', description: "Don't skip \u2014 get 401k match at minimum", where: '401k to match, then Roth IRA', timeline: '10+ years' },
        ],
        insight: "Tackle high-interest debt aggressively, but don't stop investing. Get your 401k match \u2014 it's free money.",
        projection: calculateProjection(amount * 0.35, 30),
      };
    }

    if (ef === 'not_started' || ef === 'building') {
      const b1 = ef === 'not_started' ? 35 : 25;
      const b2 = hasGoals ? 15 : 0;
      const b4 = 100 - b1 - b2;
      return {
        monthlyAmount: amount, lifeStage: stage,
        buckets: [
          { bucket: 1, name: 'SAFETY', amount: amount * b1 / 100, percent: b1, color: '#22c55e', description: 'Build emergency fund', where: 'HYSA (4-5% APY)', timeline: 'Until 3-6 months expenses', target: '$10,000 - $20,000' },
          ...(hasGoals ? [{ bucket: 2 as const, name: 'SHORT-TERM GOAL', amount: amount * 0.15, percent: 15, color: '#3b82f6', description: goalDesc, where: 'HYSA or I-Bonds', timeline: '1-5 years' }] : []),
          { bucket: 4, name: 'LONG-TERM GROWTH', amount: amount * b4 / 100, percent: b4, color: '#8b5cf6', description: 'Start investing NOW', where: '401k match \u2192 Roth IRA \u2192 VOO/SCHD', timeline: '10+ years' },
        ],
        insight: 'Build both simultaneously. Your emergency fund protects you. Your investments build your future.',
        projection: calculateProjection(amount * b4 / 100, 30),
      };
    }

    // Full emergency fund
    if (hasGoals) {
      return {
        monthlyAmount: amount, lifeStage: stage,
        buckets: [
          { bucket: 1, name: 'SAFETY', amount: 0, percent: 0, color: '#22c55e', description: '\u2713 Full \u2014 maintain only', where: 'HYSA', timeline: 'Ongoing' },
          { bucket: 2, name: 'SHORT-TERM GOAL', amount: amount * 0.35, percent: 35, color: '#3b82f6', description: goalDesc, where: 'HYSA or I-Bonds (safe, need it soon)', timeline: '1-5 years' },
          { bucket: 4, name: 'LONG-TERM GROWTH', amount: amount * 0.65, percent: 65, color: '#8b5cf6', description: 'Maximize growth', where: '401k match \u2192 Roth IRA \u2192 VOO/SCHD', timeline: '10+ years' },
        ],
        insight: "Don't stop long-term investing for short-term goals. Both matter.",
        projection: calculateProjection(amount * 0.65, 30),
      };
    }

    // Full emergency, no goals = acceleration mode
    return {
      monthlyAmount: amount, lifeStage: stage,
      buckets: [
        { bucket: 1, name: 'SAFETY', amount: 0, percent: 0, color: '#22c55e', description: '\u2713 Full \u2014 maintain only', where: 'HYSA', timeline: 'Ongoing' },
        { bucket: 4, name: 'LONG-TERM GROWTH', amount: amount, percent: 100, color: '#8b5cf6', description: 'ALL TO GROWTH \u2014 Acceleration mode!', where: '401k max \u2192 Roth IRA max \u2192 Brokerage (VOO/SCHD)', timeline: '10+ years' },
      ],
      insight: "You're in acceleration mode. Maximum compounding. Stay consistent.",
      projection: calculateProjection(amount, 30),
    };
  }

  /* ---------- BUILDING WEALTH ---------- */
  if (stage === 'building') {
    const ef = ans.emergencyFund;
    const goal = ans.majorGoal;
    const risk = ans.riskTolerance;

    let b2 = 0, b3 = 0, b2Desc = '', b3Desc = '';
    if (goal === 'college') { b3 = 25; b3Desc = "Kids' college fund"; }
    else if (goal === 'property') { b2 = 20; b2Desc = 'Home upgrade / Property fund'; }
    else if (goal === 'career') { b3 = 15; b3Desc = 'Career change flexibility'; }

    const b1 = ef === 'need_rebuild' ? 10 : 0;
    const b4 = 100 - b1 - b2 - b3;

    return {
      monthlyAmount: amount, lifeStage: stage,
      buckets: [
        ...(b1 > 0
          ? [{ bucket: 1 as const, name: 'SAFETY', amount: amount * b1 / 100, percent: b1, color: '#22c55e', description: 'Rebuild emergency fund', where: 'HYSA', timeline: 'Until 3-6 months expenses' }]
          : [{ bucket: 1 as const, name: 'SAFETY', amount: 0, percent: 0, color: '#22c55e', description: '\u2713 Full \u2014 maintain only', where: 'HYSA', timeline: 'Ongoing' }]),
        ...(b2 > 0 ? [{ bucket: 2 as const, name: 'SHORT-TERM', amount: amount * b2 / 100, percent: b2, color: '#3b82f6', description: b2Desc, where: 'HYSA, I-Bonds, Short bonds', timeline: '1-5 years' }] : []),
        ...(b3 > 0 ? [{ bucket: 3 as const, name: 'MEDIUM-TERM', amount: amount * b3 / 100, percent: b3, color: '#f59e0b', description: b3Desc, where: 'Balanced fund (60/40), Target date, 529', timeline: '5-10 years' }] : []),
        { bucket: 4, name: 'LONG-TERM GROWTH', amount: amount * b4 / 100, percent: b4, color: '#8b5cf6', description: 'Freedom Number fund', where: '401k max \u2192 Roth IRA \u2192 VOO/SCHD', timeline: '10+ years' },
      ],
      insight: risk === 'aggressive'
        ? 'You have time on your side. Stay aggressive with Bucket 4.'
        : 'Balance growth with goal-specific savings. Roll forward when goals complete.',
      projection: calculateProjection(amount * b4 / 100, 20),
    };
  }

  /* ---------- APPROACHING FREEDOM ---------- */
  if (stage === 'approaching') {
    const yrs = ans.yearsToFreedom;
    const crashNote = 'In a market downturn: Spend Bucket 1 \u2192 Refill from Bucket 2 \u2192 STOP withdrawing from Bucket 4 \u2192 Let it recover. Buckets 1-3 give you 3-5 years of runway while markets recover.';

    // 10+ years away (age 50-55): Begin building withdrawal buckets
    if (yrs === '10plus') {
      return {
        monthlyAmount: amount, lifeStage: stage,
        buckets: [
          { bucket: 1, name: 'YEAR 1 CASH', amount: amount * 0.03, percent: 3, color: '#22c55e', description: 'Start building cash cushion for retirement', where: 'HYSA / Money Market', timeline: 'Target: 1 year expenses' },
          { bucket: 2, name: 'YEARS 2-3 BUFFER', amount: amount * 0.07, percent: 7, color: '#3b82f6', description: 'Building the buffer', where: 'SCHD, Short-term bonds, CDs', timeline: 'Target: 2-3 years expenses' },
          { bucket: 3, name: 'YEARS 4-10 BRIDGE', amount: amount * 0.23, percent: 23, color: '#f59e0b', description: 'Bridge fund for market downturns', where: 'Balanced funds, Dividend stocks', timeline: '5-10 years' },
          { bucket: 4, name: 'YEARS 10+ GROWTH', amount: amount * 0.67, percent: 67, color: '#8b5cf6', description: 'Still the majority \u2014 you may live to 90+', where: 'VOO, VTI, Growth index', timeline: '10+ years' },
        ],
        insight: "Don't shift too conservative too early. You still need growth. At 55, that's 35+ years of retirement to fund.",
        crashStrategy: crashNote,
      };
    }

    // 5-10 years away (age 55-60): Accelerating transition
    if (yrs === '5to10') {
      return {
        monthlyAmount: amount, lifeStage: stage,
        buckets: [
          { bucket: 1, name: 'YEAR 1 CASH', amount: amount * 0.04, percent: 4, color: '#22c55e', description: 'Getting close to full', where: 'HYSA / Money Market', timeline: 'Target: 1 year expenses' },
          { bucket: 2, name: 'YEARS 2-3 BUFFER', amount: amount * 0.09, percent: 9, color: '#3b82f6', description: 'Building toward 2-3 years', where: 'SCHD, Short bonds, CDs', timeline: 'Target: 2-3 years expenses' },
          { bucket: 3, name: 'YEARS 4-10 BRIDGE', amount: amount * 0.27, percent: 27, color: '#f59e0b', description: 'Should be well-funded \u2014 your crash protection', where: 'Balanced funds, Dividend stocks', timeline: '5-10 years' },
          { bucket: 4, name: 'YEARS 10+ GROWTH', amount: amount * 0.60, percent: 60, color: '#8b5cf6', description: 'Still majority \u2014 funds years 10-35 of retirement', where: 'VOO, VTI, Growth index', timeline: '10+ years' },
        ],
        insight: "You're almost there. The 4-bucket system is nearly set. Accelerate building Buckets 1-3 while keeping growth dominant.",
        crashStrategy: crashNote,
      };
    }

    // Less than 5 years (age 60-65): Final transition
    if (yrs === 'less5') {
      return {
        monthlyAmount: amount, lifeStage: stage,
        buckets: [
          { bucket: 1, name: 'YEAR 1 CASH', amount: amount * 0.05, percent: 5, color: '#22c55e', description: '1 year of expenses in cash \u2014 your spending account', where: 'HYSA / Money Market', timeline: 'Year 1 expenses' },
          { bucket: 2, name: 'YEARS 2-3 BUFFER', amount: amount * 0.10, percent: 10, color: '#3b82f6', description: 'Refills Bucket 1 annually', where: 'SCHD, Short bonds', timeline: 'Years 2-3' },
          { bucket: 3, name: 'YEARS 4-10 BRIDGE', amount: amount * 0.27, percent: 27, color: '#f59e0b', description: 'Crash protection runway \u2014 6-7 years of expenses', where: 'Balanced funds, Dividend stocks', timeline: 'Years 4-10' },
          { bucket: 4, name: 'YEARS 10+ GROWTH', amount: amount * 0.58, percent: 58, color: '#8b5cf6', description: 'Longevity fund \u2014 funds 20-30 years of retirement', where: 'VOO, VTI', timeline: 'Years 10+' },
        ],
        insight: 'Almost there! Target allocation nearly achieved. Bucket 4 stays the majority because retirement can last 20-30 years.',
        crashStrategy: crashNote,
      };
    }

    // Already there / Could retire now (age 65+)
    return {
      monthlyAmount: amount, lifeStage: stage,
      buckets: [
        { bucket: 1, name: 'YEAR 1 CASH', amount: amount * 0.05, percent: 5, color: '#22c55e', description: 'Spending account \u2014 1 year of expenses', where: 'HYSA / Money Market', timeline: 'Year 1 expenses' },
        { bucket: 2, name: 'YEARS 2-3 BUFFER', amount: amount * 0.10, percent: 10, color: '#3b82f6', description: 'Refills Bucket 1 annually', where: 'SCHD, Short bonds', timeline: 'Years 2-3' },
        { bucket: 3, name: 'YEARS 4-10 BRIDGE', amount: amount * 0.27, percent: 27, color: '#f59e0b', description: 'Crash protection \u2014 don\'t touch B4 in downturns', where: 'Balanced funds, Dividend stocks', timeline: 'Years 4-10' },
        { bucket: 4, name: 'YEARS 10+ GROWTH', amount: amount * 0.58, percent: 58, color: '#8b5cf6', description: 'Still the majority \u2014 you need growth for 20-30 years', where: 'VOO, VTI', timeline: 'Years 10+' },
      ],
      insight: 'Your 4-Bucket system is set! Annual rebalance: Spend B1 \u2192 Refill from B2 \u2192 Refill B2 from B3 \u2192 Refill B3 from B4 in good years only.',
      crashStrategy: crashNote,
    };
  }

  return { monthlyAmount: amount, lifeStage: stage, buckets: [], insight: 'Unable to calculate allocation.' };
}

function calculateFlywheelData(monthly: number): FlywheelRow[] {
  const data: FlywheelRow[] = [];
  let portfolio = 0;
  const rate = 0.08;
  const divYield = 0.02;
  const milestones = [1, 5, 10, 20, 30];

  for (let year = 1; year <= 30; year++) {
    for (let m = 0; m < 12; m++) {
      portfolio += monthly;
      portfolio *= (1 + rate / 12);
    }
    if (milestones.includes(year)) {
      const annualDiv = portfolio * divYield;
      const monthlyDiv = annualDiv / 12;
      const divPct = monthly > 0 ? (monthlyDiv / monthly) * 100 : 0;
      data.push({ year, portfolio: Math.round(portfolio), annualDividends: Math.round(annualDiv), monthlyDividends: Math.round(monthlyDiv), dividendPercent: Math.round(divPct) });
    }
  }
  return data;
}

/* ============================================
   Sub-components
   ============================================ */

const StepIndicator: React.FC<{ current: number }> = ({ current }) => {
  const labels = ['Amount', 'Life Stage', 'Questions', 'Results'];
  return (
    <div className="bv-step-indicator">
      {labels.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && <div className={`bv-step-line${i <= current ? ' bv-step-line-active' : ''}`} />}
          <div className={`bv-step-item${i === current ? ' bv-step-current' : ''}${i < current ? ' bv-step-done' : ''}`}>
            <div className="bv-step-circle">{i < current ? '\u2713' : i + 1}</div>
            <span className="bv-step-label">{label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const BucketCard: React.FC<{ alloc: BucketAllocation; mode?: InvestMode }> = ({ alloc, mode = 'monthly' }) => {
  const isZero = alloc.percent === 0;
  const suffix = mode === 'monthly' ? '/mo' : '';
  return (
    <div className={`bv-bucket-card${isZero ? ' bv-bucket-zero' : ''}`} style={{ borderLeftColor: alloc.color }}>
      <div className="bv-bucket-header">
        <span className="bv-bucket-badge" style={{ background: alloc.color }}>B{alloc.bucket}</span>
        <span className="bv-bucket-name">{alloc.name}</span>
        {!isZero && <span className="bv-bucket-amount">{fmt(alloc.amount)}{suffix} ({alloc.percent}%)</span>}
      </div>
      {!isZero && (
        <div className="bv-bar-track">
          <div className="bv-bar-fill" style={{ width: `${alloc.percent}%`, background: alloc.color }} />
        </div>
      )}
      <div className="bv-bucket-details">
        <p className="bv-bucket-desc">{alloc.description}</p>
        <div className="bv-bucket-meta">
          <span><strong>Where:</strong> {alloc.where}</span>
          <span><strong>Timeline:</strong> {alloc.timeline}</span>
          {alloc.target && <span><strong>Target:</strong> {alloc.target}</span>}
        </div>
      </div>
    </div>
  );
};

const FLYWHEEL_NODES = [
  { title: 'CONSISTENT CONTRIBUTIONS', sub: 'Your monthly investment', icon: '\u{1F4B0}' },
  { title: 'BUY INDEX FUNDS + DIV STOCKS', sub: 'VOO, SCHD, Target Date', icon: '\u{1F4C8}' },
  { title: 'PORTFOLIO GROWS', sub: 'Compounding at ~8%/year', icon: '\u{1F4CA}' },
  { title: 'DIVIDENDS GENERATED', sub: '~2% yield every quarter', icon: '\u{1F4B5}' },
  { title: 'REINVEST DIVIDENDS (DRIP)', sub: 'Buys more shares automatically', icon: '\u267B\uFE0F' },
];

const FlywheelSection: React.FC<{ monthly: number }> = ({ monthly }) => {
  const data = useMemo(() => calculateFlywheelData(monthly), [monthly]);
  const last = data[data.length - 1];

  return (
    <div className="bv-flywheel-section">
      <h3 className="bv-flywheel-title">THE WEALTH FLYWHEEL</h3>
      <p className="bv-flywheel-subtitle">How Consistent Investing Builds Momentum</p>

      <div className="bv-flywheel-diagram">
        {FLYWHEEL_NODES.map((node, i) => (
          <React.Fragment key={node.title}>
            {i > 0 && <div className="bv-fw-arrow">{'\u2193'}</div>}
            <div className="bv-fw-node" style={{ animationDelay: `${i * 0.5}s` }}>
              <span className="bv-fw-icon">{node.icon}</span>
              <div className="bv-fw-text">
                <strong>{node.title}</strong>
                <span>{i === 0 ? `${fmt(monthly)}/month` : node.sub}</span>
              </div>
            </div>
          </React.Fragment>
        ))}
        <div className="bv-fw-arrow">{'\u21BB'}</div>
        <div className="bv-fw-loop">The flywheel accelerates</div>
      </div>

      <div className="bv-fw-banner">
        MORE SHARES &rarr; MORE DIVIDENDS &rarr; MORE SHARES &rarr; ...
      </div>

      {monthly > 0 && (
        <>
          <h4 className="bv-fw-table-title">Flywheel in Action: {fmt(monthly)}/month at 8% + 2% dividend yield</h4>
          <div className="bv-table-wrap">
            <table className="bv-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Portfolio</th>
                  <th>Annual Div</th>
                  <th>Monthly Div</th>
                  <th>Div vs Contrib</th>
                </tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row.year} className={row.dividendPercent >= 100 ? 'bv-highlight-row' : ''}>
                    <td>{row.year}</td>
                    <td>{fmt(row.portfolio)}</td>
                    <td>{fmt(row.annualDividends)}</td>
                    <td>{fmt(row.monthlyDividends)}</td>
                    <td>{row.dividendPercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {last && last.dividendPercent >= 100 && (
            <div className="bv-fw-callout">
              By year {last.year}, dividends ({fmt(last.monthlyDividends)}/mo) EXCEED your contributions!
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ============================================
   Main Component
   ============================================ */

export const BucketVisualizer: React.FC = () => {
  const { user, token } = useAuth();
  const [mode, setMode] = useState<InvestMode>('monthly');
  const [step, setStep] = useState(0);
  const [amountStr, setAmountStr] = useState('');
  const [lifeStage, setLifeStage] = useState<LifeStage | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const amount = parseFloat(amountStr) || 0;

  const result = useMemo<AllocationResult | null>(() => {
    if (step < 3 || !lifeStage) return null;
    return calculateAllocation(amount, lifeStage, answers);
  }, [step, amount, lifeStage, answers]);

  const growthBucket = result?.buckets.find(b => b.name.includes('GROWTH') || b.name.includes('LONG-TERM'));
  const flywheelMonthly = growthBucket ? growthBucket.amount : 0;

  const questions = lifeStage ? STAGE_QUESTIONS[lifeStage] : [];
  const allAnswered = questions.every(q => answers[q.key]);

  const handleAnswer = (key: string, value: string) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const isPremier = user?.effectiveTier === 'premier' || user?.effectiveTier === 'pro';

  const fetchSummary = useCallback(async () => {
    if (!token || !result || !lifeStage || !isPremier) return;
    setLoadingSummary(true);
    setSummaryError('');
    try {
      const res = await bucketService.getSummary(token, {
        monthlyAmount: result.monthlyAmount,
        lifeStage: result.lifeStage,
        buckets: result.buckets.map(b => ({
          name: b.name, amount: b.amount, percent: b.percent,
          description: b.description, where: b.where, timeline: b.timeline,
        })),
        insight: result.insight,
        projection: result.projection,
        answers,
      });
      setSummary(res.summary);
    } catch (err: any) {
      setSummaryError(err?.response?.data?.error || 'Failed to generate summary. Please try again.');
    } finally {
      setLoadingSummary(false);
    }
  }, [token, result, lifeStage, answers, isPremier]);

  // Auto-fetch summary when results appear (premier only)
  useEffect(() => {
    if (step === 3 && result && !summary && !loadingSummary && !summaryError && isPremier) {
      fetchSummary();
    }
  }, [step, result, summary, loadingSummary, summaryError, fetchSummary, isPremier]);

  const generatePDF = () => {
    if (!result || !lifeStage) return;

    const doc = new jsPDF();
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(26, 26, 46);
    doc.text('4-Bucket Allocation Report', 105, yPos, { align: 'center' });
    yPos += 10;

    // Date + stage
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, yPos, { align: 'center' });
    yPos += 6;
    doc.text(`${STAGE_INFO[lifeStage].title}  |  ${fmt(result.monthlyAmount)}${mode === 'monthly' ? '/month' : ' (one-time)'}`, 105, yPos, { align: 'center' });
    yPos += 12;

    // AI Summary (if available)
    if (summary) {
      doc.setFontSize(14);
      doc.setTextColor(26, 115, 232);
      doc.text('AI Summary', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setTextColor(50);
      const plainSummary = summary.replace(/[#*_`]/g, '').replace(/\n{2,}/g, '\n');
      const splitSummary = doc.splitTextToSize(plainSummary, 180);
      doc.text(splitSummary, 14, yPos);
      yPos += splitSummary.length * 5 + 10;
    }

    // Bucket Allocation table
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Bucket Allocation', 14, yPos);
    yPos += 5;

    const activeBuckets = result.buckets.filter(b => b.percent > 0);
    autoTable(doc, {
      startY: yPos,
      head: [['Bucket', 'Allocation', 'Description', 'Where', 'Timeline']],
      body: activeBuckets.map(b => [
        `B${b.bucket} ${b.name}`,
        `${fmt(b.amount)}${mode === 'monthly' ? '/mo' : ''} (${b.percent}%)`,
        b.description,
        b.where,
        b.timeline,
      ]),
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    // Key Insight
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Key Insight', 14, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(50);
    const splitInsight = doc.splitTextToSize(result.insight, 180);
    doc.text(splitInsight, 14, yPos);
    yPos += splitInsight.length * 5 + 10;

    // Crash strategy
    if (result.crashStrategy) {
      doc.setFontSize(14);
      doc.setTextColor(245, 158, 11);
      doc.text('Crash Protection Strategy', 14, yPos);
      yPos += 8;
      doc.setFontSize(10);
      doc.setTextColor(50);
      const splitCrash = doc.splitTextToSize(result.crashStrategy, 180);
      doc.text(splitCrash, 14, yPos);
      yPos += splitCrash.length * 5 + 10;
    }

    // Projection
    if (result.projection) {
      doc.setFontSize(14);
      doc.setTextColor(26, 115, 232);
      doc.text(`${result.projection.years}-Year Projection (Growth bucket at 8%)`, 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Total Invested', fmt(result.projection.totalInvested)],
          ['Projected Value', fmt(result.projection.projectedValue)],
          ['Monthly Dividends', `${fmt(result.projection.monthlyDividends)}/mo`],
        ],
        theme: 'striped',
        headStyles: { fillColor: [26, 115, 232] },
        margin: { left: 14, right: 14 },
      });

      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Flywheel table
    if (flywheelMonthly > 0) {
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      doc.setFontSize(14);
      doc.setTextColor(26, 115, 232);
      doc.text('Wealth Flywheel Projection', 14, yPos);
      yPos += 5;

      const fwData = calculateFlywheelData(flywheelMonthly);
      autoTable(doc, {
        startY: yPos,
        head: [['Year', 'Portfolio', 'Annual Dividends', 'Monthly Dividends', 'Div vs Contrib']],
        body: fwData.map(r => [
          r.year.toString(),
          fmt(r.portfolio),
          fmt(r.annualDividends),
          fmt(r.monthlyDividends),
          `${r.dividendPercent}%`,
        ]),
        theme: 'striped',
        headStyles: { fillColor: [26, 115, 232] },
        margin: { left: 14, right: 14 },
        styles: { fontSize: 9 },
      });
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
      doc.text('Generated by Wealth Financial Systems - 4-Bucket Visualizer', 105, 295, { align: 'center' });
    }

    doc.save(`bucket-allocation-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const reset = () => {
    setStep(0);
    setMode('monthly');
    setAmountStr('');
    setLifeStage(null);
    setAnswers({});
    setSummary('');
    setSummaryError('');
  };

  return (
    <div className="bv-container">
      <Navigation />

      <div className="bv-content">
        <div className="bv-page-header">
          <h2>4-Bucket System Visualizer</h2>
        </div>

        <div className="tool-purpose-banner">
          <span className="tpb-icon">🪣</span>
          <span>
            <strong>Use this tool if</strong> you want to know how to split your money across the 4 buckets — whether it's a monthly contribution or a one-time lump sum — based on your life stage and goals.{' '}
            Looking to calculate a retirement date or model long-term portfolio growth?{' '}
            Use <strong>FIRE Strategy</strong> or <strong>Lifecycle Planner</strong> instead.
          </span>
        </div>

        {(
          <>
            <StepIndicator current={step} />

            {/* Step 0: Amount */}
            {step === 0 && (
              <div className="bv-card">
                <div className="bv-mode-toggle">
                  <button
                    className={`bv-mode-btn${mode === 'monthly' ? ' bv-mode-active' : ''}`}
                    onClick={() => { setMode('monthly'); setAmountStr(''); }}
                  >
                    Monthly Investment
                  </button>
                  <button
                    className={`bv-mode-btn${mode === 'onetime' ? ' bv-mode-active' : ''}`}
                    onClick={() => { setMode('onetime'); setAmountStr(''); }}
                  >
                    One-Time Lump Sum
                  </button>
                </div>
                <h3>{mode === 'monthly' ? 'How much can you invest each month?' : 'How much do you have to invest?'}</h3>
                <div className="bv-amount-wrap">
                  <span className="bv-amount-prefix">$</span>
                  <input
                    type="number"
                    className="bv-amount-input"
                    placeholder={mode === 'monthly' ? '500' : '10000'}
                    value={amountStr}
                    onChange={e => setAmountStr(e.target.value)}
                    min="1"
                    step={mode === 'monthly' ? '50' : '500'}
                    autoFocus
                  />
                  {mode === 'monthly' && <span className="bv-amount-suffix">/month</span>}
                </div>
                <button className="bv-btn bv-btn-primary" disabled={amount <= 0} onClick={() => setStep(1)}>
                  Continue &rarr;
                </button>
              </div>
            )}

            {/* Step 1: Life Stage */}
            {step === 1 && (
              <div className="bv-card">
                <h3>Where are you in your wealth-building journey?</h3>
                <div className="bv-stages-grid">
                  {(['starting', 'building', 'approaching'] as LifeStage[]).map(key => {
                    const info = STAGE_INFO[key];
                    return (
                      <button
                        key={key}
                        className={`bv-stage-card${lifeStage === key ? ' bv-stage-selected' : ''}`}
                        onClick={() => { setLifeStage(key); setAnswers({}); setStep(2); }}
                      >
                        <span className="bv-stage-emoji">{info.emoji}</span>
                        <strong className="bv-stage-title">{info.title}</strong>
                        <span className="bv-stage-meta">{info.age}</span>
                        <span className="bv-stage-meta">{info.netWorth}</span>
                        <span className="bv-stage-meta">{info.focus}</span>
                      </button>
                    );
                  })}
                </div>
                <button className="bv-btn bv-btn-back" onClick={() => setStep(0)}>&larr; Back</button>
              </div>
            )}

            {/* Step 2: Questions */}
            {step === 2 && lifeStage && (
              <div className="bv-card">
                <h3>A few quick questions:</h3>
                {questions.map(q => (
                  <div className="bv-question-group" key={q.key}>
                    <label className="bv-question-label">{q.label}</label>
                    <div className="bv-radio-group">
                      {q.options.map(opt => (
                        <button
                          key={opt.value}
                          className={`bv-radio-option${answers[q.key] === opt.value ? ' bv-radio-selected' : ''}`}
                          onClick={() => handleAnswer(q.key, opt.value)}
                        >
                          <span className="bv-radio-dot" />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="bv-btn-row">
                  <button className="bv-btn bv-btn-back" onClick={() => setStep(1)}>&larr; Back</button>
                  <button className="bv-btn bv-btn-primary" disabled={!allAnswered} onClick={() => setStep(3)}>
                    Show My Bucket Allocation &rarr;
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Results */}
            {step === 3 && result && (
              <div className="bv-results">
                {/* Allocation header */}
                <div className="bv-results-header">
                  <h3>YOUR {fmt(result.monthlyAmount)}{mode === 'monthly' ? '/MONTH' : ''} BUCKET ALLOCATION</h3>
                  {lifeStage && <span className="bv-stage-badge">{STAGE_INFO[lifeStage].emoji} {STAGE_INFO[lifeStage].title}</span>}
                </div>

                {/* AI Summary */}
                {isPremier ? (
                  <div className="bv-summary-section">
                    {loadingSummary && (
                      <div className="bv-summary-card">
                        <div className="bv-summary-loading">
                          <div className="bv-summary-spinner" />
                          <span>Generating your personalized summary...</span>
                        </div>
                      </div>
                    )}
                    {summaryError && (
                      <div className="bv-summary-error">
                        <p>{summaryError}</p>
                        <button className="bv-btn bv-btn-secondary" onClick={fetchSummary}>Retry</button>
                      </div>
                    )}
                    {summary && (
                      <div className="bv-summary-card">
                        <strong>AI SUMMARY</strong>
                        <div className="bv-summary-content">
                          <ReactMarkdown>{summary}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bv-summary-upgrade">
                    <span className="bv-summary-upgrade-icon">&#x2728;</span>
                    <div>
                      <strong>AI-Powered Summary</strong>
                      <p>Upgrade to Premier for a personalized AI analysis of your bucket allocation.</p>
                    </div>
                    <Link to="/upgrade" className="bv-btn bv-btn-primary">Upgrade</Link>
                  </div>
                )}

                {/* Bucket cards */}
                <div className="bv-bucket-list">
                  {result.buckets.map((alloc, i) => (
                    <BucketCard key={`${alloc.bucket}-${i}`} alloc={alloc} mode={mode} />
                  ))}
                </div>

                {/* Insight */}
                <div className="bv-insight">
                  <strong>KEY INSIGHT</strong>
                  <p>{result.insight}</p>
                </div>

                {/* Crash strategy */}
                {result.crashStrategy && (
                  <div className="bv-crash-strategy">
                    <strong>CRASH PROTECTION STRATEGY</strong>
                    <p>{result.crashStrategy}</p>
                  </div>
                )}

                {/* Projection (monthly mode only) */}
                {mode === 'monthly' && result.projection && (
                  <div className="bv-projection">
                    <h4>{result.projection.years}-YEAR PROJECTION (Growth bucket at 8%)</h4>
                    <div className="bv-projection-grid">
                      <div className="bv-projection-stat">
                        <span className="bv-proj-label">Total Invested</span>
                        <span className="bv-proj-value">{fmt(result.projection.totalInvested)}</span>
                      </div>
                      <div className="bv-projection-stat">
                        <span className="bv-proj-label">Projected Value</span>
                        <span className="bv-proj-value bv-proj-highlight">{fmt(result.projection.projectedValue)}</span>
                      </div>
                      <div className="bv-projection-stat">
                        <span className="bv-proj-label">Monthly Dividends</span>
                        <span className="bv-proj-value">{fmt(result.projection.monthlyDividends)}/mo</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Flywheel (monthly mode only) */}
                {mode === 'monthly' && flywheelMonthly > 0 && <FlywheelSection monthly={flywheelMonthly} />}

                {/* Actions */}
                <div className="bv-actions">
                  <button className="bv-btn bv-btn-back" onClick={reset}>Start Over</button>
                  <button className="bv-btn bv-btn-secondary" onClick={generatePDF}>Download PDF</button>
                  <Link to="/fire-calculator" className="bv-btn bv-btn-primary">FIRE Investment Strategy &rarr;</Link>
                  <Link to="/playbook" className="bv-btn bv-btn-secondary">Read the Playbook</Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
